import ConfirmDeleteModal from "@/components/ConfirmDeleteModal.tsx";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE, MAX_FILE_SIZE_DISPLAY } from "@/config/fileUpload";
import type { S3SignedUrlAndFilename } from "@/mutations/document.ts";
import { useDeleteDocumentMutation } from "@/mutations/document.ts";
import { useCreateApiDocumentsMutation } from "@/mutations/useCreateApiDocumentsMutation.ts";
import { useGenerateMultiplePreSignedPutUrl } from "@/mutations/useGenerateMultiplePreSignedPutUrl.ts";
import type { DocumentCategory, DocumentData } from "@/types/document.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    MenuItem,
    Stack,
    type SxProps,
    TextField,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import type * as React from "react";
import { type ChangeEvent, useCallback, useEffect, useState } from "react";
import type { FileWithPath } from "react-dropzone";
import { useDropzone } from "react-dropzone";

const tags = [
    "Accounting",
    "Case Details",
    "Client Contact",
    "Closing",
    "Companies",
    "Disbursement",
    "File Activity / Review",
    "Intake",
    "Liability",
    "Loan",
    "MedPay",
    "Negotiation",
    "Person Details",
    "Providers",
    "Subrogation",
    "Termination Process",
    "UM/UIM",
];

type Props = {
    idGlobalUUID: string;
    category: DocumentCategory;
    description: string;
    resourceName: string;
    forceTag?: string;
    sx?: SxProps;
};

const MultipleMediaUpload = ({
    idGlobalUUID,
    category,
    description,
    resourceName,
    forceTag,
    sx,
}: Props): React.ReactElement => {
    const generatePreSignedPutUrl = useGenerateMultiplePreSignedPutUrl();
    const createDocumentsMutation = useCreateApiDocumentsMutation();
    const deleteDocumentMutation = useDeleteDocumentMutation();
    const [filesUpload, setFilesUpload] = useState<File[]>([]);
    const [documentsData, setDocumentsData] = useState<DocumentData[]>([]);
    const [waitingForUpload, setWaitingForUpload] = useState(false);
    const [waitingForDelete, setWaitingForDelete] = useState(false);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [displayResourceUrls, setDisplayResourceUrls] = useState<string[]>([]);
    const [open, setOpen] = useState(false);
    const [tag, setTag] = useState(forceTag);
    const [tagError, setTagError] = useState("");
    const [fileError, setFileError] = useState("");
    const [uploading, setUploading] = useState(false);

    const handleConfirm = () => {
        if (tag && displayResourceUrls.length > 0 && documentsData.length > 0) {
            // Create new array with updated tags and update state
            const documentsWithTags = documentsData.map((doc) => ({
                ...doc,
                tag: tag,
            }));
            setDocumentsData(documentsWithTags);
            generatePreSignedPutUrl.mutate(documentsWithTags);
            return;
        }

        setTagError(tag === "" ? "Tag is required." : "");
        setFileError(
            !(displayResourceUrls.length > 0 && documentsData.length > 0)
                ? "File is required."
                : "",
        );
    };

    const handleCloseModal = () => {
        setTag(forceTag);
        setTagError("");
        setFileError("");
        setDocumentsData([]);
        setFilesUpload([]);
        // Revoke all object URLs to prevent memory leaks
        displayResourceUrls.forEach((url) => URL.revokeObjectURL(url));
        setDisplayResourceUrls([]);
        setOpen(false);
        generatePreSignedPutUrl.reset();
    };

    const onDrop = useCallback((acceptedFiles: FileWithPath[]) => {
        if (acceptedFiles.length) {
            const filesToUpload = acceptedFiles;

            const documentsData = filesToUpload.map((file) => ({
                idGlobalUUID: idGlobalUUID,
                category: category,
                filename: file.name,
                description: description,
                size: file.size,
                mime: file.type,
                resourceName: `${resourceName}/${file.name}`,
                tag: null,
            }));

            setFilesUpload(filesToUpload);
            setDocumentsData(documentsData);

            // Create object URLs for preview - much faster than readAsDataURL
            const previewUrls = filesToUpload.map((file) => URL.createObjectURL(file));
            setDisplayResourceUrls(previewUrls);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true,
        accept: ACCEPTED_FILE_TYPES,
        minSize: 0,
        maxSize: MAX_FILE_SIZE,
    });

    const handleDeleteDocument = (index: number) => {
        const updatedFiles = [...filesUpload];
        updatedFiles.splice(index, 1);
        setFilesUpload(updatedFiles);

        const updatedDocuments = [...documentsData];
        updatedDocuments.splice(index, 1);
        setDocumentsData(updatedDocuments);

        const updatedUrls = [...displayResourceUrls];
        // Revoke the object URL to free memory
        URL.revokeObjectURL(updatedUrls[index]);
        updatedUrls.splice(index, 1);
        setDisplayResourceUrls(updatedUrls);
    };

    useEffect(() => {
        if (!generatePreSignedPutUrl.isSuccess || documentsData.length === 0 || uploading) {
            return;
        }

        if (!generatePreSignedPutUrl.data?.uploads) {
            enqueueSnackbar("Error: No upload URLs received", { variant: "error" });
            setUploading(false);
            return;
        }

        // Map documents to their renamed versions from server
        const documentDataWithFileRenames = documentsData.map((doc) => {
            const renamedUpload = generatePreSignedPutUrl.data.uploads.find(
                (upload) => upload.originalFilename === doc.filename,
            );

            return renamedUpload
                ? {
                      ...doc,
                      filename: renamedUpload.filename,
                      resourceName: `${doc.resourceName.substring(
                          0,
                          doc.resourceName.lastIndexOf("/"),
                      )}/${renamedUpload.filename}`,
                  }
                : doc;
        });

        // Handle any errors in generating URLs
        if (generatePreSignedPutUrl.data.errors && generatePreSignedPutUrl.data.errors.length > 0) {
            generatePreSignedPutUrl.data.errors.forEach((error) => {
                if (error) {
                    enqueueSnackbar(`Error generating URL: ${error}`, { variant: "error" });
                }
            });

            // Filter to only successful uploads
            const successfulItems = documentDataWithFileRenames
                .map((doc, index) => ({
                    doc,
                    file: filesUpload[index],
                    upload: generatePreSignedPutUrl.data.uploads.find(
                        (u) => u.filename === doc.filename,
                    ),
                }))
                .filter((item) => item.upload?.url);

            if (successfulItems.length === 0) {
                setUploading(false);
                return;
            }

            const successfulDocuments = successfulItems.map((item) => item.doc);
            const successfulFiles = successfulItems.map((item) => item.file);
            const successfulUploads = successfulItems.map(
                (item) => item.upload,
            ) as S3SignedUrlAndFilename[];

            setUploading(true);
            void uploadDocuments(successfulDocuments, successfulFiles, successfulUploads);
        } else {
            // No errors, proceed with all documents
            setUploading(true);
            void uploadDocuments(
                documentDataWithFileRenames,
                filesUpload,
                generatePreSignedPutUrl.data.uploads,
            );
        }
    }, [generatePreSignedPutUrl.isSuccess, documentsData]);

    const uploadDocuments = async (
        docsToUpload: DocumentData[],
        filesToUpload: File[],
        uploads: S3SignedUrlAndFilename[],
    ) => {
        if (!uploads || uploads.length === 0) {
            enqueueSnackbar("No valid upload URLs available", { variant: "error" });
            console.error("No valid upload URLs available.");
            setUploading(false);
            return;
        }

        try {
            const uploadPromises = docsToUpload.map((doc, index) => {
                const upload = uploads.find((u) => u.filename === doc.filename);

                if (!upload?.url) {
                    return Promise.reject(new Error(`No upload URL for file ${doc.filename}`));
                }

                return new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open("PUT", upload.url);
                    xhr.onload = () =>
                        xhr.status === 200
                            ? resolve(xhr.response)
                            : reject(new Error(`Upload failed for file ${doc.filename}`));
                    xhr.onerror = () => reject(new Error(`Upload failed for file ${doc.filename}`));
                    xhr.send(filesToUpload[index]);
                });
            });

            await Promise.all(uploadPromises);

            createDocumentsMutation.mutate(docsToUpload, {
                onSuccess: () => {
                    const successCount = docsToUpload.length;
                    const totalCount = documentsData.length;

                    enqueueSnackbar(`Successfully uploaded ${successCount} document(s)`, {
                        variant: "success",
                    });

                    if (successCount !== totalCount) {
                        enqueueSnackbar(
                            `${totalCount - successCount} document(s) failed to upload`,
                            { variant: "warning" },
                        );
                        console.error(`${totalCount - successCount} document(s) failed to upload`);
                    }
                    handleCloseModal();
                },
                onError: () => {
                    enqueueSnackbar("Error adding documents to database", { variant: "error" });
                    console.error("Error adding documents to database");
                },
            });
        } catch (error) {
            enqueueSnackbar("Some documents failed to upload", { variant: "error" });
            console.error("Some documents failed to upload");
        } finally {
            setUploading(false);
        }
    };

    useEffect(() => {
        if (createDocumentsMutation.status === "pending") {
            setWaitingForUpload(true);
        }

        if (waitingForUpload && createDocumentsMutation.isSuccess) {
            enqueueSnackbar("Documents Uploaded Successfully", { variant: "success" });
            setWaitingForUpload(false);
            handleCloseModal();
        }

        if (waitingForUpload && createDocumentsMutation.isError) {
            enqueueSnackbar("Documents Upload Failed (API)", { variant: "error" });
            setWaitingForUpload(false);
            console.error("Documents Upload Failed (API)");
        }
    }, [createDocumentsMutation]);

    useEffect(() => {
        if (deleteDocumentMutation.status === "pending") {
            setWaitingForDelete(true);
        }

        if (waitingForDelete && deleteDocumentMutation.isSuccess) {
            enqueueSnackbar("Document Deleted Successfully", { variant: "success" });
            console.error("Document Deleted Successfully");
            setWaitingForDelete(false);
        }

        if (waitingForDelete && deleteDocumentMutation.isError) {
            enqueueSnackbar("Document Delete Failed", { variant: "error" });
            console.error("Document Delete Failed");
            setWaitingForDelete(false);
        }
    }, [deleteDocumentMutation]);

    return (
        <Stack
            sx={{
                flexDirection: "row",
                justifyContent: "start",
                position: "relative",
            }}
        >
            <Button
                variant={"contained"}
                sx={{
                    ml: 2,
                    minWidth: "120px",
                }}
                onClick={() => {
                    setOpen(true);
                }}
            >
                New Media
            </Button>
            <Dialog open={open} fullWidth={true} maxWidth="sm" onClose={handleCloseModal}>
                <DialogTitle>Add Media</DialogTitle>
                <DialogContent sx={{ minHeight: "300px" }}>
                    <Stack
                        sx={{
                            p: 1,
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Box>
                            <Stack
                                sx={{
                                    p: 1,
                                    flexDirection: "column",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                }}
                            >
                                <TextField
                                    select
                                    value={tag}
                                    name={"tag"}
                                    label={"Tag"}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                        setTag(event.target.value);
                                    }}
                                    error={tagError !== ""}
                                    helperText={tagError !== "" ? tagError : ""}
                                    sx={{
                                        minWidth: "355px",
                                        width: "100%",
                                        mb: 2,
                                    }}
                                >
                                    {tags.map((tag) => (
                                        <MenuItem key={tag} value={tag}>
                                            {tag}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                            <Stack
                                {...getRootProps()}
                                sx={{
                                    p: 1,
                                    flexDirection: "column",
                                    border: "1px dashed orange",
                                    justifyContent: "center",
                                    ...sx,
                                }}
                            >
                                <input {...getInputProps()} />
                                {filesUpload.map((file, index) => {
                                    const isImage = file.type.startsWith("image/");
                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                position: "relative",
                                                mb: 1,
                                                p: isImage ? 0 : 1,
                                                bgcolor: isImage ? "transparent" : "#f5f5f5",
                                                borderRadius: 1,
                                            }}
                                        >
                                            {isImage ? (
                                                <Box
                                                    component="img"
                                                    alt="Media"
                                                    src={displayResourceUrls[index]}
                                                    sx={{
                                                        height: "100%",
                                                        width: "100%",
                                                    }}
                                                />
                                            ) : (
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1}
                                                >
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <Box sx={{ fontWeight: "bold" }}>
                                                            {file.name}
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                fontSize: "0.875rem",
                                                                color: "text.secondary",
                                                            }}
                                                        >
                                                            {(file.size / (1024 * 1024)).toFixed(2)}{" "}
                                                            MB
                                                        </Box>
                                                    </Box>
                                                </Stack>
                                            )}
                                            <IconButton
                                                aria-label="delete-media"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleDeleteDocument(index);
                                                }}
                                                sx={{
                                                    position: "absolute",
                                                    color: "#aaa",
                                                    top: -8,
                                                    right: 0,
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    );
                                })}
                                {filesUpload.length > 0 && <Divider />}
                                {filesUpload.length === 0 && isDragActive ? (
                                    <Box sx={{ textAlign: "center", p: 1 }}>
                                        Drop the files here ...
                                    </Box>
                                ) : (
                                    <Box sx={{ textAlign: "center", p: 1 }}>
                                        Drag 'n' drop files here, or click to select files.
                                    </Box>
                                )}
                            </Stack>
                            {fileError && (
                                <Stack
                                    sx={{
                                        flexDirection: "row",
                                        justifyContent: "flex-start",
                                        width: "100%",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            mt: 2,
                                            fontSize: "12px",
                                            color: "#d32f2f",
                                            pl: 3,
                                        }}
                                    >
                                        {fileError}
                                    </Box>
                                </Stack>
                            )}
                            <Box sx={{ mt: 2 }}>
                                File size is limited to {MAX_FILE_SIZE_DISPLAY}
                            </Box>
                            {uploading && (
                                <Stack
                                    sx={{
                                        flexDirection: "column",
                                        justifyContent: "flex-start",
                                        width: "100%",
                                    }}
                                >
                                    <Box sx={{ mt: 2 }}>Uploading</Box>
                                    <LinearProgress />
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Cancel</Button>
                    <Button onClick={handleConfirm}>Add Media</Button>
                </DialogActions>
            </Dialog>
            <ConfirmDeleteModal
                open={openConfirm}
                onClose={() => {
                    setOpenConfirm(false);
                }}
                onConfirm={() => {
                    handleDeleteDocument(0);
                    setOpenConfirm(false);
                }}
                title={"Confirm Delete"}
            />
        </Stack>
    );
};

export default MultipleMediaUpload;
